/**
 * Email Detail View Component
 *
 * CENTER panel for Step 3 (viewing single email).
 * Shows full email content with Reply/Forward/Delete actions.
 *
 * Layout:
 * - Header: Date/time, close button
 * - Subject: Re: Subject line
 * - From/To info on same line
 * - Content: HTML body with images
 * - Attachments: File cards with icons and sizes
 * - Footer: Reply (filled), Forward (outlined), Delete (outlined)
 */

import React from "react";
import { EmailMessage } from "./types";
import { X, Reply, Forward, Trash2, FileText, Image, Download } from "lucide-react";

interface EmailDetailViewProps {
  email: EmailMessage;
  onReply: () => void;
  onForward: () => void;
  onClose: () => void;
  onOpenInsights: () => void;
  isMobile: boolean;
  onDelete?: () => void;
}

/**
 * Email Detail View - Shows full email content
 */
export const EmailDetailView: React.FC<EmailDetailViewProps> = ({
  email,
  onReply,
  onForward,
  onClose,
  onOpenInsights,
  isMobile,
  onDelete,
}) => {
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    return `${month} ${day}, ${year} • ${hour12}:${minutes} ${ampm}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith("image/")) {
      return <Image size={20} className="text-blue-500" />;
    }
    if (type === "application/pdf") {
      return <FileText size={20} className="text-red-500" />;
    }
    return <FileText size={20} className="text-gray-500 dark:text-gray-400" />;
  };

  const subjectDisplay = email.subject?.startsWith("Re:")
    ? email.subject
    : `Re: ${email.subject}`;

  return (
    <div className="flex-1 bg-white dark:bg-gray-900 flex flex-col h-full overflow-hidden border-l border-gray-200 dark:border-gray-800">
      {/* Header - Date, Subject, From/To, Close */}
      <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800">
        {/* Date and Close */}
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500">
            {formatDateTime(email.sentAt)}
          </span>
          <button
            onClick={onClose}
            className="p-1 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Subject */}
        <h1 className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {subjectDisplay}
        </h1>

        {/* From / To */}
        <div className="text-[12px] text-gray-500 dark:text-gray-400">
          <span className="text-[#7B7B7B] font-semibold dark:text-gray-500">From:</span>{" "}
          <span className="text-gray-600 dark:text-gray-300">{email.from.name || email.from.email}</span>
          {" <"}<span className="text-[#7B7B7B] font-semibold dark:text-gray-500">{email.from.email}</span>{">"}
          <span className="mx-3"></span>
          <span className="text-[#7B7B7B] font-semibold dark:text-gray-500">To:</span>{" "}
          <span className="text-gray-600 dark:text-gray-300">{email.to[0]?.name || email.to[0]?.email}</span>
          {email.to[0]?.email && (
            <>{" <"}<span className="text-[#7B7B7B] font-semibold dark:text-gray-500">{email.to[0].email}</span>{">"}</>
          )}
        </div>
      </div>

      {/* Email Content - Scrollable */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {/* Email Body */}
        <div
          className="text-[14px] text-gray-700 dark:text-gray-200 dark:prose-invert leading-relaxed prose prose-sm max-w-none mb-6"
          dangerouslySetInnerHTML={{ __html: email.body }}
        />

        {/* Attachments */}
        {email.attachments && email.attachments.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-800">
            <p className="text-[13px] font-medium text-gray-700 dark:text-gray-200 mb-3">
              Attachments
            </p>
            <div className="flex flex-wrap gap-3">
              {email.attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-w-[180px]"
                >
                  {getFileIcon(attachment.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-gray-700 dark:text-gray-200 font-medium truncate">
                      {attachment.name}
                    </p>
                    <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">
                      {formatFileSize(attachment.size)}
                    </p>
                  </div>
                  <Download size={16} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons Footer */}
      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-3">
          {/* Reply Button - Filled */}
          <button
            onClick={onReply}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-[13px] font-medium transition-colors duration-150 bg-[#7C83FD] text-white hover:bg-[#6b73e8]"
          >
            <Reply size={16} />
            Reply
          </button>

          {/* Forward Button - Outlined */}
          <button
            onClick={onForward}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-[13px] font-medium transition-colors duration-150 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Forward size={16} />
            Forward
          </button>

          {/* Delete Button - Outlined */}
          <button
            onClick={onDelete}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-[13px] font-medium transition-colors duration-150 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-red-500 hover:border-red-300 dark:hover:border-red-500/50"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>

      {/* Mobile Insights Button */}
      {isMobile && (
        <button
          onClick={onOpenInsights}
          className="fixed bottom-24 right-4 p-3 bg-[#7C83FD] text-white rounded-full shadow-lg"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
        </button>
      )}
    </div>
  );
};
